import ContentContainer, {
  ContentContainerStyle,
} from 'remote-com-shared/components/ContentContainer';
import { fluidProp, pixelToRem, unlessProp, whenProp } from 'remote-com-shared/style/helpers';
import { sharedTransition } from 'remote-com-shared/style/motion';
import styled, { css } from 'styled-components';
import { Swiper } from 'swiper/react';

import { OverviewTitleComponent } from './index';

import { desktopBreakpoint, fromDesktopBreakpoint } from '@/src/domains/shared/breakpoints';
import { defaultAnimation } from '@/src/style/motion';

export const SectionWrapper = styled.section`
  --sectionSliderColor: var(--colors-bayoux);
  --sectionSliderColorHighlight: var(--sectionSliderColor);
  --sectionSliderTitleColor: var(--colors-darkBlue);
  --sectionSliderNavigationColor: var(--colors-irisBlue);
  --sectionSliderNavigationBackground: var(--colors-moonRaker);
  --sectionSliderNavigationActiveBackground: var(--colors-irisBlue);
  --sectionSliderHighlightColor: var(--colors-irisBlue);
  --sectionSliderHighlightColorTranslucent: rgba(255, 255, 255, 0.5);
  --sectionSliderBackgroundImage: var(--colors-zircon);
  --sectionSliderProgressBackgroundHighlight: var(--sectionSliderHighlightColor);
  --sectionSliderLayoutLimit: 516px;

  color: var(--sectionSliderColor);
  background-image: var(--sectionSliderBackgroundImage);
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
  ${fluidProp(['padding-bottom'], '60px', '100px')}
`;

export const SectionHeader = styled.header`
  text-align: center;
  ${fluidProp(['padding-top'], '60px', '160px')}
  ${fluidProp(['padding-bottom'], '10px', '100px')}

  ${defaultAnimation}
`;

export const LabelComponent = styled.div`
  margin: 0 auto 18px auto;
  text-align: center;

  & > small {
    ${({ theme }) => theme.typography.subHead}
    text-transform: uppercase;
    color: var(--colors-irisBlue);
  }
`;

export const DescriptionComponent = styled.div`
  ${({ theme }) => theme.typography.bodyLead}
  ${fluidProp(['max-width'], '320px', '750px')}
  color: var(--colors-bayoux);
  margin: 0 auto;
`;

export const SwiperSlider = styled(Swiper)`
  position: relative;
  display: flex;
  flex-grow: 1;
  width: 100%;
`;

export const Slide = styled.article`
  position: relative;
  box-sizing: border-box;
  overflow: hidden;
  flex-basis: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  ${fluidProp(['padding-top'], '60px', '180px')}
  ${fluidProp(['padding-bottom'], '80px', '180px')}

  ${fromDesktopBreakpoint(css`
    margin: 0;
    height: 90vh;
    /* width: calc(100% + 130px); */
  `)}
`;

export const SlideContentContainer = styled(ContentContainer)`
  ${desktopBreakpoint(css`
    display: flex;
    justify-content: center;
  `)}
  ${fromDesktopBreakpoint(css`
    display: flex;
    ${({ $isEven }) =>
      $isEven
        ? css`
            justify-content: start;
          `
        : css`
            justify-content: end;
          `}
  `)}
  && {
    flex-grow: 1;
  }
`;

export const SlideContainer = styled.div`
  ${fromDesktopBreakpoint(css`
    max-width: min(calc(40% - 25px), var(--sectionSliderLayoutLimit));
  `)}
`;

export const SlideHeader = styled.header`
  display: flex;
  flex-direction: column;
  margin-bottom: 28px;
  gap: 16px;
  align-items: baseline;
`;

export const HeaderIcon = styled.div`
  max-width: 108px;
  margin: 0;
  width: 55px;
`;

export const SlideTitle = styled.div`
  ${({ theme }) => theme.typography.scale3}
  ${fluidProp(['font-size'], '24px', '28px')}
  color: var(--sectionSliderTitleColor);

  > * {
    font-size: inherit;
    font-weight: inherit;
    margin: inherit;
  }
`;

export const SlideDescription = styled.div`
  > ul {
    list-style: none;
    padding-left: 10px;
    ${({ theme }) => theme.typography.body}
    line-height: 2;
    margin-bottom: 15px;

    li::before {
      content: '•';
      color: var(--colors-turquoise);
      margin-right: 10px;
    }
  }
`;
export const SlideDescriptionNote = styled.span`
  ${({ theme }) => theme.typography.bodyXS}
`;
export const SlideDescriptionStrong = styled.strong`
  color: var(--colors-turquoise);
`;
export const SlideDescriptionOrderedList = styled.ol`
  position: relative;
  padding-left: 0;

  > li {
    counter-increment: step-counter;
    list-style: none;
    padding: 10px 0 10px 60px;
    margin: 15px 0;
    position: relative;
    overflow: hidden;
    display: flex;
    align-content: center;

    &:before {
      content: counter(step-counter);
      color: var(--colors-turquoise);
      padding: 5px;
      border-radius: 50%;
      border: 2px var(--colors-turquoise) solid;
      position: absolute;
      z-index: -1;
      left: 0;
      top: 0;
      text-align: center;
      width: 28px;
      height: 28px;
    }
  }
`;

export const SlideIllustration = styled.img`
  display: none;
  position: absolute;
  z-index: -1;
  justify-self: flex-end;
  top: 50%;
  transform: translateY(-50%);
  width: auto;
  height: auto;
  min-height: 50vh;
  max-height: min(90vh, 50vw);
  /* max-height: 60vh; */
  ${({ $isEven }) =>
    $isEven
      ? css`
          right: 0;
        `
      : css`
          left: 0;
        `}

  ${fromDesktopBreakpoint(css`
    display: block;
  `)}
`;

export const SlideLabelsWrapper = styled.div`
  ${fromDesktopBreakpoint(css`
    position: relative;
    top: -120px;
    padding-bottom: 120px;
  `)}
`;

export const SlidesLabels = styled.div`
  position: absolute;
  bottom: 30px;
  display: flex;
  align-items: center;
  color: var(--sectionSliderNavigationColor);
  font-weight: 500;
  font-size: ${pixelToRem('13px')};
  text-transform: uppercase;
  width: max-content;
  margin: 0 auto;
  left: 0;
  right: 0;
  ${ContentContainerStyle};

  ${fromDesktopBreakpoint(css`
    width: unset;
    justify-content: space-between;
    bottom: auto;
    padding-top: 75px;
    padding-bottom: 30px;
  `)}
`;

export const ProgressBarHighlight = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background-color: var(--sectionSliderProgressBackgroundHighlight);
  width: 0%;
  transition: width 0s linear;
`;

export const ProgressBarBackground = styled.span`
  position: relative;
  height: 2px;
  flex-grow: 1;
  margin: 0 24px;
  background-color: var(--sectionSliderProgressBackgroundHighlight);
  pointer-events: none;
  display: none;

  ${fromDesktopBreakpoint(css`
    display: block;
  `)}

  ${whenProp(
    '$dirty',
    css`
      background-color: var(--sectionSliderProgressBackgroundHighlight);
    `
  )}

  ${unlessProp(
    '$active',
    css`
      opacity: 0.65;
    `
  )}
`;

export const SlideLabel = styled.button.attrs({
  type: 'button',
})`
  padding: 0;
  color: inherit;
  font-size: inherit;
  font-weight: inherit;
  text-transform: inherit;
  border: 2px solid var(--sectionSliderNavigationActiveBackground);
  background-color: transparent;

  /* To allow buttons to be clickable */
  z-index: 2;

  opacity: 0.3;
  height: 32px;
  width: 32px;
  border-radius: 50%;
  position: relative;

  & > p {
    margin-top: 0;
    position: absolute;
    top: 45px;
    left: 50%;
    transform: translateX(-50%);
    text-align: center;
    white-space: pre;
    display: none;

    ${fromDesktopBreakpoint(css`
      display: unset;
    `)}
  }

  ${desktopBreakpoint(css`
    padding: 0;
    opacity: 1;
    text-indent: -999em;
    width: 10px;
    height: 6px;
    border-radius: 6px;
    background-color: var(--sectionSliderNavigationBackground);
    ${sharedTransition('width, background-color')}
    margin: 0 4px;

    ${whenProp(
      '$active',
      css`
        width: 34px;
        background-color: var(--sectionSliderNavigationActiveBackground);
      `
    )}
  `)}

  &:first-of-type + ${ProgressBarBackground} ${ProgressBarHighlight} {
    width: 0%;
  }

  ${({ $visible, $dirty }) => {
    if ($visible && !$dirty)
      return css`
        &:first-of-type + ${ProgressBarBackground} ${ProgressBarHighlight} {
          transition-duration: 5s;
          width: 100%;
        }
      `;
    return null;
  }}

  ${ProgressBarBackground} {
    background-color: var(--sectionSliderNavigationBackground);
  }

  ${whenProp(
    '$active',
    css`
      opacity: 1;
      background-color: var(--sectionSliderNavigationActiveBackground);
      color: var(--colors-turquoise);

      & > p {
        color: var(--sectionSliderNavigationActiveBackground);
      }

      ${unlessProp(
        '$dirty',
        css`
          & + ${ProgressBarBackground} ${ProgressBarHighlight} {
            transition-duration: 5s;
            width: 100%;
          }
        `
      )}
      & ~ ${ProgressBarBackground} {
        background-color: var(--sectionSliderNavigationBackground);
      }
    `
  )}
`;

export const CarouselSectionWrapper = styled(SectionWrapper)`
  && {
    --sectionSliderColor: var(--colors-bayoux);
    --sectionSliderColorHighlight: var(--sectionSliderColor);
    --sectionSliderTitleColor: var(--colors-darkBlue);
    --sectionSliderNavigationColor: var(--newBrandColor);
    --sectionSliderNavigationBackground: var(--colors-moonRaker);
    --sectionSliderNavigationActiveBackground: var(--newBrandColor);
    --sectionSliderHighlightColor: var(--newBrandColor);
    --sectionSliderHighlightColorTranslucent: rgba(255, 255, 255, 0.5);
    --sectionSliderBackgroundImage: var(--colors-zircon);
    --sectionSliderProgressBackgroundHighlight: var(--sectionSliderHighlightColor);
    --sectionDarkToneColor: #00183e;

    ${whenProp(
      '$light',
      css`
        --sectionSliderBackgroundImage: none;
        background: #f8f5ff;
      `
    )}

    ${whenProp(
      '$fontBrand',
      css`
        background-color: #fbf9ff;
        ${OverviewTitleComponent}, ${SlideTitle} {
          > * {
            font-family: var(--typography-fontBossa);
          }
        }
        ${DescriptionComponent}, ${SlideDescription}, ${SlideLabel} {
          > * {
            font-family: var(--typography-fontBrand);
          }
        }
      `
    )}

    ${whenProp(
      '$darkTone',
      css`
        --sectionSliderBackgroundImage: none;

        background-color: #ebf2ff;
        position: relative;
        z-index: 1;
        overflow: hidden;

        ${fromDesktopBreakpoint(css`
          clip-path: ellipse(120% 100% at 50% 0%);
          ${fluidProp('padding-bottom', '150px', '210px')};
        `)}

        ${LabelComponent} > small {
          color: var(--sectionDarkToneColor);
          font-weight: 400;
          font-family: var(--typography-fontBossa);
        }

        ${DescriptionComponent} {
          color: var(--sectionDarkToneColor);
          opacity: 0.65;
        }

        ${SlideLabelsWrapper} {
          margin-bottom: 72px;
        }

        ${SlidesLabels} {
          --sectionSliderNavigationColor: var(--colors-darkBlue);
          --sectionSliderNavigationActiveBackground: var(--colors-darkBlue);
          --sectionSliderProgressBackgroundHighlight: ${({ theme }) => theme.colors.darkBlue_75};
          --sectionSliderNavigationBackground: ${({ theme }) => theme.colors.darkBlue_30};

          ${SlideLabel} {
            ${fromDesktopBreakpoint(css`
              color: #ebf2ff;
              position: relative;
              padding: 5px;
              box-sizing: border-box;
              height: 28px;
              width: 28px;
              background-color: var(--colors-darkBlue);

              &::before {
                content: '';
                position: absolute;
                left: -8px;
                top: -8px;
                width: 40px;
                height: 40px;
                border-radius: 50%;
                padding: 5px;
                border: 2px solid var(--colors-darkBlue);
              }

              > * {
                color: var(--colors-darkBlue);
              }
            `)}
          }
        }

        ${Slide} {
          max-width: 918px;
          max-height: 400px;
          margin: 0 auto;
          background-color: var(--colors-blank);
          position: relative;
          border-radius: 24px;

          img {
            z-index: 0;
            max-height: unset;
            min-height: unset;

            &:nth-child(odd) {
              left: 20%;
            }
          }

          ${HeaderIcon} {
            display: none;
          }
        }

        ${SlideLabel} {
          p {
            width: 200px;
            white-space: unset;
          }
        }
      `
    )}
  }

  ${LabelComponent} > small {
    color: var(--newBrandColor);
  }
`;
